import { type SelectHTMLAttributes, useId } from 'react';

import { Icon } from '@/components/ui/Icon';
import { cn } from '@/utils/cn';

import { Field } from './Field';
import styles from './Field.module.css';
import { fieldDescriptionIds } from './field-ids';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: readonly SelectOption[];
  /** Shown as the first, empty choice — e.g. "Not set". */
  placeholder?: string;
  hint?: string | undefined;
  error?: string | undefined;
  fieldClassName?: string | undefined;
}

export function SelectField({
  label,
  options,
  placeholder,
  hint,
  error,
  id,
  required,
  className,
  fieldClassName,
  ...selectProps
}: SelectFieldProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  return (
    <Field
      label={label}
      htmlFor={selectId}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
    >
      <div className={styles.control}>
        <select
          id={selectId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={fieldDescriptionIds(selectId, hint, error)}
          className={cn(styles.input, styles.select, error && styles.invalid, className)}
          {...selectProps}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <Icon name="chevron-down" size={18} className={styles.selectChevron} />
      </div>
    </Field>
  );
}
